import React, { useState, useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';

import Box              from '@mui/material/Box';
import Grid             from '@mui/material/Grid';
import Typography       from '@mui/material/Typography';
import Tooltip          from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert            from '@mui/material/Alert';
import Chip             from '@mui/material/Chip';
import Divider          from '@mui/material/Divider';
import Dialog           from '@mui/material/Dialog';
import DialogTitle      from '@mui/material/DialogTitle';
import DialogContent    from '@mui/material/DialogContent';
import DialogActions    from '@mui/material/DialogActions';
import Button           from '@mui/material/Button';
import IconButton       from '@mui/material/IconButton';
import Table            from '@mui/material/Table';
import TableBody        from '@mui/material/TableBody';
import TableCell        from '@mui/material/TableCell';
import TableContainer   from '@mui/material/TableContainer';
import TableHead        from '@mui/material/TableHead';
import TableRow         from '@mui/material/TableRow';
import Paper            from '@mui/material/Paper';
import TablePagination  from '@mui/material/TablePagination';
import TextField        from '@mui/material/TextField';
import MenuItem         from '@mui/material/MenuItem';
import Select           from '@mui/material/Select';
import FormControl      from '@mui/material/FormControl';
import InputLabel       from '@mui/material/InputLabel';
import InputAdornment   from '@mui/material/InputAdornment';

import CloseIcon           from '@mui/icons-material/Close';
import SearchIcon          from '@mui/icons-material/Search';
import GavelIcon           from '@mui/icons-material/Gavel';
import PersonIcon          from '@mui/icons-material/Person';
import InfoOutlinedIcon    from '@mui/icons-material/InfoOutlined';
import DownloadIcon        from '@mui/icons-material/Download';

import Card     from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import moneyFormat from 'helpers/moneyFormat';
import { useExport } from 'context/ExportContext';

const CALF_GREEN      = '#8ebf63';
const CALF_GREEN_DARK = '#39a82d';
const CALF_BLACK      = '#161616';
const CALF_LIGHT      = '#bee59a';
const GREY_TEXT       = '#757575';

const ESTUDIO_COLORS = {
  'Dr. Marcelo Daniel Iñiguez':  '#2d6a1f',
  'Dra. Rosas Priscila Raquel':  '#8ebf63',
  'Dra. Camila Arias':           '#c47d1a',
  '(en blanco)':                 '#9e9e9e',
};

const MARCA_DESC = {
  AE:  'Acuerdo Extrajudicial',
  CON: 'Convenio',
  GAE: 'Gestión Abogado Externo',
  JUD: 'Judicial',
};

const MARCA_COLORS = {
  AE:  { bg: '#e8f5e9', color: '#1b5e20' },
  CON: { bg: '#e3f2fd', color: '#0d47a1' },
  GAE: { bg: '#f3e5f5', color: '#4a148c' },
  JUD: { bg: '#fce4ec', color: '#b71c1c' },
};

const fmt = (val) => {
  if (val == null || isNaN(val)) return '—';
  if (Math.abs(val) >= 1e6) return `$${(val / 1e6).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}M`;
  return moneyFormat(val);
};
const fmtFull = (val) => (val == null || isNaN(val) ? '—' : moneyFormat(val));

function procesarTotales(datos) {
  const estudios = {};
  datos.forEach(({ estudio, marca, servicio, importe, registros }) => {
    if (!estudios[estudio]) {
      estudios[estudio] = { estudio, total: 0, energia: 0, sepelios: 0, registros: 0, marcas: {} };
    }
    const e = estudios[estudio];
    e.total     += importe;
    e.registros += registros;
    if (servicio === 'Energia')  e.energia  += importe;
    if (servicio === 'Sepelios') e.sepelios += importe;
    if (!e.marcas[marca]) e.marcas[marca] = 0;
    e.marcas[marca] += importe;
  });
  return Object.values(estudios).sort((a, b) => b.total - a.total);
}

function totalPorMarca(datos) {
  const marcas = {};
  datos.forEach(({ marca, importe }) => {
    if (!marcas[marca]) marcas[marca] = 0;
    marcas[marca] += importe;
  });
  return Object.entries(marcas).sort((a, b) => b[1] - a[1]);
}

function EstudioCard({ data, onClick, loading }) {
  if (loading) {
    return (
      <Box sx={{ border: `1px solid ${CALF_LIGHT}`, borderRadius: 2, p: 2, minHeight: 150,
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={24} sx={{ color: CALF_GREEN }} />
      </Box>
    );
  }
  if (!data) return null;
  const color = ESTUDIO_COLORS[data.estudio] || CALF_GREEN;
  return (
    <Tooltip title={`Click para ver los ${data.registros.toLocaleString('es-AR')} comprobantes`} placement="top">
      <Box onClick={() => onClick(data)}
        sx={{
          cursor: 'pointer', borderRadius: 2, p: 2, bgcolor: '#fff',
          border: `1px solid ${CALF_LIGHT}`,
          borderLeft: `5px solid ${color}`,
          boxShadow: '0 1px 4px rgba(0,0,0,.07)',
          transition: 'box-shadow .15s, transform .15s',
          '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,.13)', transform: 'translateY(-2px)' },
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <PersonIcon sx={{ color, fontSize: 20 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, color: CALF_BLACK, textTransform: 'uppercase', letterSpacing: .5 }}>
            {data.estudio === '(en blanco)' ? 'Sin estudio asignado' : data.estudio}
          </Typography>
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 800, color, lineHeight: 1.1 }}>
          {fmt(data.total)}
        </Typography>
        <Typography variant="caption" sx={{ color: GREY_TEXT, display: 'block', mb: 1 }}>
          {data.registros.toLocaleString('es-AR')} comprobantes
        </Typography>
        <Divider sx={{ my: 1, borderColor: '#f0f0f0' }} />
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          {data.energia > 0 && (
            <Chip label={`Energía ${fmt(data.energia)}`} size="small"
              sx={{ fontSize: '0.68rem', height: 18, bgcolor: '#f0f4ea', color: '#27500a' }} />
          )}
          {data.sepelios > 0 && (
            <Chip label={`Sepelios ${fmt(data.sepelios)}`} size="small"
              sx={{ fontSize: '0.68rem', height: 18, bgcolor: '#f5f5f5', color: GREY_TEXT }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {Object.entries(data.marcas).sort((a, b) => b[1] - a[1]).map(([marca, imp]) => {
            const mc = MARCA_COLORS[marca] || { bg: '#f5f5f5', color: GREY_TEXT };
            return (
              <Tooltip key={marca} title={`${MARCA_DESC[marca] || marca}: ${fmtFull(imp)}`} placement="top">
                <Chip label={`${marca} ${fmt(imp)}`} size="small"
                  sx={{ fontSize: '0.67rem', height: 18, bgcolor: mc.bg, color: mc.color, cursor: 'help' }} />
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    </Tooltip>
  );
}

function DonutEstudios({ estudios }) {
  const labels  = estudios.map(e => e.estudio === '(en blanco)' ? 'Sin estudio' : e.estudio);
  const series  = estudios.map(e => Math.max(0, e.total));
  const colors  = estudios.map(e => ESTUDIO_COLORS[e.estudio] || CALF_GREEN);
  const total   = series.reduce((a, b) => a + b, 0);

  const options = {
    chart: { type: 'donut', fontFamily: 'inherit' },
    labels,
    colors,
    legend: { position: 'bottom', fontSize: '11px' },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val.toFixed(1)}%`,
      style: { fontSize: '11px', fontWeight: 600 },
      dropShadow: { enabled: false },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '68%',
          labels: {
            show: true,
            value: {
              show: true, fontSize: '14px', fontWeight: 700, color: CALF_BLACK,
              formatter: (val) => fmt(Number(val)),
            },
            total: {
              show: true, label: 'Total EJ', fontSize: '12px', fontWeight: 700,
              color: CALF_BLACK, formatter: () => fmt(total),
            },
          },
        },
      },
    },
    tooltip: { y: { formatter: (val) => fmtFull(val) } },
    stroke: { width: 2 },
  };

  return <ReactApexChart options={options} series={series} type="donut" height={300} />;
}

function BarMarcas({ datos }) {
  const marcas  = totalPorMarca(datos);
  const labels  = marcas.map(([m]) => `${m} — ${MARCA_DESC[m] || m}`);
  const series  = marcas.map(([, v]) => Math.round(v));
  const colors  = marcas.map(([m]) => MARCA_COLORS[m]?.color || GREY_TEXT);

  const options = {
    chart: { type: 'bar', fontFamily: 'inherit', toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 3, dataLabels: { position: 'top' } } },
    dataLabels: {
      enabled: true,
      formatter: (val) => fmt(val),
      offsetX: 8,
      style: { fontSize: '11px', colors: [CALF_BLACK] },
    },
    xaxis: { categories: labels, labels: { formatter: (v) => fmt(v) } },
    colors,
    tooltip: { y: { formatter: (val) => fmtFull(val) } },
    grid: { borderColor: '#f0f0f0' },
  };

  return (
    <ReactApexChart
      options={options}
      series={[{ name: 'Importe', data: series }]}
      type="bar"
      height={Math.max(160, marcas.length * 55 + 60)}
    />
  );
}

function DrillDownModal({ open, onClose, estudio }) {
  const [rows,          setRows]          = useState([]);
  const [total,         setTotal]         = useState(0);
  const [page,          setPage]          = useState(0);
  const [rowsPP,        setRowsPP]        = useState(50);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [sinResultados, setSinResultados] = useState(false);
  const [filtroNombre,  setFiltroNombre]  = useState('');
  const [filtroPeriodo, setFiltroPeriodo] = useState('');
  const [filtroEstado,  setFiltroEstado]  = useState('');
  const [filtroMarca,   setFiltroMarca]   = useState('');

  const { startExport } = useExport();

  const fetchDetalle = async (pg, sz, nombre, periodo, estado, marca) => {
    if (!estudio) return;
    setLoading(true); setError(null); setSinResultados(false);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/deuda-ej/detalle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': localStorage.getItem('token') },
        body: JSON.stringify({
          // La tarjeta "(en blanco)" agrupa los registros con SubMarca NULL (Mantis #48).
          // Mandamos un flag explícito: con filtroEstudio vacío el backend NO filtraba
          // y el modal devolvía TODOS los estudios, sin coincidir con la tarjeta.
          filtroEstudio:    estudio === '(en blanco)' ? '' : estudio,
          filtroSinEstudio: estudio === '(en blanco)',
          filtroMarca:   marca,
          filtroNombre:  nombre,
          filtroPeriodo: periodo,
          filtroEstado:  estado,
          page: pg, size: sz,
        }),
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const json = await res.json();
      setRows(json.data ?? []);
      setTotal(json.total ?? 0);
      if ((json.total ?? 0) === 0) setSinResultados(true);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && estudio) {
      setFiltroNombre(''); setFiltroPeriodo(''); setFiltroEstado(''); setFiltroMarca('');
      setSinResultados(false); setPage(0);
      fetchDetalle(0, rowsPP, '', '', '', '');
    }
    if (!open) { setRows([]); setTotal(0); setPage(0); setSinResultados(false); }
  }, [open, estudio]);

  const ejecutarBusqueda = () => {
    setPage(0);
    fetchDetalle(0, rowsPP, filtroNombre, filtroPeriodo, filtroEstado, filtroMarca);
  };

  const handleLimpiar = () => {
    setFiltroNombre(''); setFiltroPeriodo(''); setFiltroEstado(''); setFiltroMarca('');
    setSinResultados(false); setPage(0);
    fetchDetalle(0, rowsPP, '', '', '', '');
  };

  const handleExport = (format) => {
    const params = new URLSearchParams({ format });
    if (estudio === '(en blanco)') params.set('filtroSinEstudio', 'true');
    else if (estudio)             params.set('filtroEstudio', estudio);
    if (filtroMarca)         params.set('filtroMarca', filtroMarca);
    if (filtroNombre.trim()) params.set('filtroNombre', filtroNombre.trim());
    if (filtroPeriodo)       params.set('filtroPeriodo', filtroPeriodo);
    if (filtroEstado)        params.set('filtroEstado', filtroEstado);
    const url = `${process.env.REACT_APP_API_URL}/deuda-ej/export?${params}`;
    startExport({
      format,
      label: `Deuda EJ ${estudio === '(en blanco)' ? 'Sin estudio' : estudio}`,
      customUrl: url,
    });
  };

  const hayFiltros = filtroNombre || filtroPeriodo || filtroEstado || filtroMarca;
  const color = ESTUDIO_COLORS[estudio] || CALF_GREEN;

  const COLS = [
    { id: 'persona',          label: 'Persona',     width: 70  },
    { id: 'nombre',           label: 'Nombre',      width: 200 },
    { id: 'domicilio',        label: 'Domicilio',   width: 200 },
    { id: 'comprobante',      label: 'Comprobante', width: 140 },
    { id: 'fecha_comprobante',label: 'F. Cpte.',    width: 90  },
    { id: 'importe',          label: 'Importe',     width: 130, align: 'right' },
    { id: 'fecha_vto',        label: 'Vto.',        width: 90  },
    { id: 'periodo_vto',      label: 'Período',     width: 90  },
    { id: 'categoria',        label: 'Tarifa',      width: 130 },
    { id: 'marca',            label: 'Marca',       width: 70  },
    { id: 'estado_deuda',     label: 'Estado',      width: 110 },
    { id: 'dias_interes',     label: 'Días',        width: 60, align: 'right' },
    { id: 'servicio',         label: 'Servicio',    width: 80  },
    { id: 'localidad',        label: 'Localidad',   width: 120 },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: `3px solid ${color}`, pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <GavelIcon sx={{ color }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: CALF_BLACK, lineHeight: 1.1 }}>
              {estudio === '(en blanco)' ? 'Sin estudio asignado' : estudio}
            </Typography>
            <Typography variant="caption" sx={{ color: GREY_TEXT }}>
              {total.toLocaleString('es-AR')} comprobantes en estudios jurídicos
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', gap: 1.5, p: 1.5, bgcolor: '#f9f9f9',
          borderBottom: '1px solid #eee', flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" placeholder="Buscar por nombre... (Enter)"
            value={filtroNombre} onChange={(e) => setFiltroNombre(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') ejecutarBusqueda(); }}
            sx={{ minWidth: 260, bgcolor: '#fff' }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: GREY_TEXT }} /></InputAdornment>,
              endAdornment: filtroNombre ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={ejecutarBusqueda} sx={{ color: CALF_GREEN_DARK }}>
                    <SearchIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140, bgcolor: '#fff' }}>
            <InputLabel>Marca</InputLabel>
            <Select value={filtroMarca} label="Marca"
              onChange={(e) => { setFiltroMarca(e.target.value); setPage(0); fetchDetalle(0, rowsPP, filtroNombre, filtroPeriodo, filtroEstado, e.target.value); }}>
              <MenuItem value="">Todas</MenuItem>
              {Object.entries(MARCA_DESC).map(([k, v]) => (
                <MenuItem key={k} value={k}>
                  <Tooltip title={v} placement="right"><span>{k} — {v}</span></Tooltip>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160, bgcolor: '#fff' }}>
            <InputLabel>Período</InputLabel>
            <Select value={filtroPeriodo} label="Período"
              onChange={(e) => { setFiltroPeriodo(e.target.value); setPage(0); fetchDetalle(0, rowsPP, filtroNombre, e.target.value, filtroEstado, filtroMarca); }}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="Anteriores">Anteriores</MenuItem>
              {Array.from({ length: 36 }, (_, i) => {
                const d = new Date();
                d.setMonth(d.getMonth() - i);
                const val = `P${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                return <MenuItem key={val} value={val}>{val}</MenuItem>;
              })}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180, bgcolor: '#fff' }}>
            <InputLabel>Estado de Deuda</InputLabel>
            <Select value={filtroEstado} label="Estado de Deuda"
              onChange={(e) => { setFiltroEstado(e.target.value); setPage(0); fetchDetalle(0, rowsPP, filtroNombre, filtroPeriodo, e.target.value, filtroMarca); }}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="Deuda Vencida">Deuda Vencida</MenuItem>
              <MenuItem value="Deuda no Vencida">Deuda no Vencida</MenuItem>
            </Select>
          </FormControl>
          {hayFiltros && (
            <Button size="small" onClick={handleLimpiar} sx={{ color: GREY_TEXT, fontSize: '0.75rem', textTransform: 'none' }}>
              Limpiar filtros
            </Button>
          )}
          <Typography variant="caption" sx={{ color: GREY_TEXT, ml: 'auto' }}>
            {total.toLocaleString('es-AR')} registros encontrados
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {sinResultados && hayFiltros && !loading && (
          <Alert severity="info" sx={{ m: 2 }}>
            No se encontraron comprobantes con los filtros aplicados. Revisá los criterios o limpiá los filtros.
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: CALF_GREEN }} />
          </Box>
        )}

        {!loading && !sinResultados && (
          <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 'calc(90vh - 280px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {COLS.map(col => (
                    <TableCell key={col.id} align={col.align || 'left'} sx={{
                      width: col.width, minWidth: col.width, fontWeight: 700, fontSize: '0.75rem',
                      bgcolor: '#f5f5f5', color: CALF_BLACK,
                      borderBottom: `3px solid ${color}`, py: 1, whiteSpace: 'nowrap',
                    }}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => (
                  <TableRow key={`${row.persona}-${row.comprobante}-${idx}`} hover sx={{
                    bgcolor: idx % 2 === 0 ? '#fff' : '#f7faf3',
                    '&:hover': { bgcolor: '#e8f4d9' },
                  }}>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{row.persona}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', minWidth: 200 }}>{row.nombre}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', minWidth: 200 }}>{row.domicilio}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem', fontFamily: 'monospace' }}>{row.comprobante}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{row.fecha_comprobante}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.78rem', fontWeight: 600,
                      color: row.importe < 0 ? '#d32f2f' : CALF_GREEN_DARK }}>{fmtFull(row.importe)}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{row.fecha_vto}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{row.periodo_vto}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{row.categoria}</TableCell>
                    <TableCell>
                      {row.marca ? (
                        <Tooltip title={MARCA_DESC[row.marca] || row.marca}>
                          <Chip label={row.marca} size="small" sx={{
                            fontSize: '0.68rem', height: 18,
                            bgcolor: MARCA_COLORS[row.marca]?.bg || '#f5f5f5',
                            color:   MARCA_COLORS[row.marca]?.color || GREY_TEXT,
                            cursor: 'help',
                          }} />
                        </Tooltip>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip label={row.estado_deuda || '—'} size="small" sx={{
                        fontSize: '0.68rem', height: 18,
                        bgcolor: row.estado_deuda === 'Deuda Vencida' ? '#fce4ec' : '#e3f2fd',
                        color:   row.estado_deuda === 'Deuda Vencida' ? '#b71c1c' : '#0d47a1',
                      }} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{row.dias_interes}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{row.servicio}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{row.localidad}</TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={COLS.length} align="center" sx={{ py: 4, color: GREY_TEXT }}>
                      Sin resultados
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #f0f0f0', px: 2, py: 1 }}>
        <TablePagination component="div" count={total} page={page}
          onPageChange={(_, p) => { setPage(p); fetchDetalle(p, rowsPP, filtroNombre, filtroPeriodo, filtroEstado, filtroMarca); }}
          rowsPerPage={rowsPP}
          onRowsPerPageChange={(e) => { const sz = +e.target.value; setRowsPP(sz); setPage(0); fetchDetalle(0, sz, filtroNombre, filtroPeriodo, filtroEstado, filtroMarca); }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          labelRowsPerPage="Filas:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count.toLocaleString('es-AR')}`}
          sx={{ flex: 1, '.MuiTablePagination-toolbar': { minHeight: 40 } }}
        />
        <Tooltip title="Descargar todos los registros filtrados en CSV" placement="top">
          <span>
            <Button variant="outlined" size="small"
              disabled={total === 0 || loading}
              onClick={() => handleExport('csv')}
              sx={{ ml: 1, borderColor: CALF_GREEN, color: CALF_GREEN_DARK,
                '&:hover': { borderColor: CALF_GREEN_DARK, bgcolor: '#f0f8e8' } }}>
              <DownloadIcon sx={{ fontSize: 16, mr: 0.5 }} />
              CSV
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Descargar en Excel (máx. 1.048.576 filas)" placement="top">
          <span>
            <Button variant="outlined" size="small"
              disabled={total === 0 || loading}
              onClick={() => handleExport('xlsx')}
              sx={{ ml: 0.5, borderColor: CALF_GREEN, color: CALF_GREEN_DARK,
                '&:hover': { borderColor: CALF_GREEN_DARK, bgcolor: '#f0f8e8' } }}>
              <DownloadIcon sx={{ fontSize: 16, mr: 0.5 }} />
              XLSX
            </Button>
          </span>
        </Tooltip>
        <Button onClick={onClose} variant="outlined" size="small" sx={{ ml: 2 }}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function DeudaEstudiosJuridicos() {
  const [datos,     setDatos]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEst,  setModalEst]  = useState(null);

  const { startExport } = useExport();

  const handleExportTodos = (format) => {
    const url = `${process.env.REACT_APP_API_URL}/deuda-ej/export?format=${format}`;
    startExport({ format, label: 'Deuda EJ (cartera completa)', customUrl: url });
  };

  useEffect(() => {
    const fetchTotales = async () => {
      setLoading(true); setError(null);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/deuda-ej/totales`, {
          headers: { 'Authorization': localStorage.getItem('token') },
        });
        if (!res.ok) throw new Error(`Error ${res.status}`);
        const json = await res.json();
        setDatos(json.datos || []);
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    };
    fetchTotales();
  }, []);

  const estudios      = procesarTotales(datos);
  const totalGeneral  = estudios.reduce((s, e) => s + e.total, 0);
  const totalEnergia  = estudios.reduce((s, e) => s + e.energia, 0);
  const totalSepelios = estudios.reduce((s, e) => s + e.sepelios, 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', pt: { xs: '100px', md: '120px' }, px: 0 }}>
      <Card>
        <CardBody>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <GavelIcon sx={{ color: CALF_GREEN_DARK }} />
              <Typography variant="h5" sx={{ fontWeight: 800, color: CALF_BLACK }}>
                Deuda en Estudios Jurídicos
              </Typography>
              <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                <Tooltip title="Exportar toda la cartera de Estudios Jurídicos a CSV" placement="top">
                  <span>
                    <Button variant="outlined" size="small" disabled={loading || datos.length === 0}
                      onClick={() => handleExportTodos('csv')}
                      sx={{ borderColor: CALF_GREEN, color: CALF_GREEN_DARK,
                        '&:hover': { borderColor: CALF_GREEN_DARK, bgcolor: '#f0f8e8' } }}>
                      <DownloadIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      CSV
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Exportar toda la cartera de Estudios Jurídicos a Excel" placement="top">
                  <span>
                    <Button variant="outlined" size="small" disabled={loading || datos.length === 0}
                      onClick={() => handleExportTodos('xlsx')}
                      sx={{ borderColor: CALF_GREEN, color: CALF_GREEN_DARK,
                        '&:hover': { borderColor: CALF_GREEN_DARK, bgcolor: '#f0f8e8' } }}>
                      <DownloadIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      XLSX
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ color: GREY_TEXT }}>
              Cartera cedida a estudios externos · marcas{' '}
              {Object.entries(MARCA_DESC).map(([k, v]) => (
                <Tooltip key={k} title={v} placement="top">
                  <span style={{ fontWeight: 600, cursor: 'help', marginRight: 6 }}>{k}</span>
                </Tooltip>
              ))}
              · incluye todos los períodos · click en tarjeta para ver comprobantes
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} lg={8}>
              <Grid container spacing={2}>
                {loading
                  ? [1, 2, 3].map(i => (
                      <Grid item xs={12} sm={6} key={i}>
                        <EstudioCard loading />
                      </Grid>
                    ))
                  : estudios.map(e => (
                      <Grid item xs={12} sm={6} key={e.estudio}>
                        <EstudioCard data={e}
                          onClick={(d) => { setModalEst(d.estudio); setModalOpen(true); }}
                          loading={false} />
                      </Grid>
                    ))
                }
              </Grid>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Box sx={{ border: `1px solid ${CALF_LIGHT}`, borderRadius: 2, p: 2, bgcolor: '#fff',
                boxShadow: '0 1px 4px rgba(0,0,0,.07)', height: '100%',
                display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: CALF_BLACK, mb: 1 }}>
                  Distribución por estudio
                </Typography>
                {loading
                  ? <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CircularProgress sx={{ color: CALF_GREEN }} />
                    </Box>
                  : estudios.length > 0 && <DonutEstudios estudios={estudios} />
                }
              </Box>
            </Grid>
          </Grid>

          {!loading && datos.length > 0 && (
            <Box sx={{ border: `1px solid ${CALF_LIGHT}`, borderRadius: 2, p: 2, bgcolor: '#fff',
              boxShadow: '0 1px 4px rgba(0,0,0,.07)', mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: CALF_BLACK, mb: 1 }}>
                Deuda por marca
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                {Object.entries(MARCA_DESC).map(([k, v]) => (
                  <Tooltip key={k} title={v} placement="top">
                    <Chip label={k} size="small"
                      sx={{ fontSize: '0.7rem', height: 20,
                        bgcolor: MARCA_COLORS[k]?.bg, color: MARCA_COLORS[k]?.color, cursor: 'help' }} />
                  </Tooltip>
                ))}
              </Box>
              <BarMarcas datos={datos} />
            </Box>
          )}

          {!loading && estudios.length > 0 && (
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', p: 2,
              bgcolor: '#f8fdf4', border: `1px solid ${CALF_LIGHT}`, borderRadius: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: GREY_TEXT, textTransform: 'uppercase', letterSpacing: .5 }}>
                  Total Estudios Jurídicos
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: CALF_GREEN_DARK }}>
                  {fmtFull(totalGeneral)}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box>
                <Typography variant="caption" sx={{ color: GREY_TEXT, textTransform: 'uppercase', letterSpacing: .5 }}>
                  Energía
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#444' }}>
                  {fmtFull(totalEnergia)}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box>
                <Typography variant="caption" sx={{ color: GREY_TEXT, textTransform: 'uppercase', letterSpacing: .5 }}>
                  Sepelios
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#444' }}>
                  {fmtFull(totalSepelios)}
                </Typography>
              </Box>
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <InfoOutlinedIcon sx={{ fontSize: 14, color: GREY_TEXT }} />
                <Typography variant="caption" sx={{ color: GREY_TEXT }}>
                  Incluye todos los períodos, sin exclusión de mes actual
                </Typography>
              </Box>
            </Box>
          )}

        </CardBody>
      </Card>

      <DrillDownModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        estudio={modalEst}
      />
    </Box>
  );
}